import type { ColumnDef } from '@tanstack/react-table';
import { BadgeDollarSign } from 'lucide-react';
import * as React from 'react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  StatusBadge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { etiquetaUnidad, PriceAdjustDialog } from './PriceAdjustDialog';
import { useAdjustPrice, useCatalogProjects, useCatalogUnits } from './use-catalog';
import type { CatalogUnit, UnitStatus, UnitType } from '../../../types/admin';
import { formatArea, formatCOP } from '../../separations/money';
import type { AccesoAdmin } from '../acceso-admin';

const PAGE_SIZE = 20;

/** Filtros con `<select>` nativo y `aria-label` (Web 03 §2.3, L-007). */
const SELECT_CLASS =
  'h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const TIPOS: Array<{ valor: UnitType; etiqueta: string }> = [
  { valor: 'APARTAMENTO', etiqueta: 'Apartamento' },
  { valor: 'CASA', etiqueta: 'Casa' },
  { valor: 'PARQUEADERO', etiqueta: 'Parqueadero' },
  { valor: 'DEPOSITO', etiqueta: 'Depósito' },
  { valor: 'LOCAL', etiqueta: 'Local' },
];
const ETIQUETA_TIPO = Object.fromEntries(TIPOS.map((t) => [t.valor, t.etiqueta])) as Record<UnitType, string>;

const ESTADOS: Array<{ valor: UnitStatus; etiqueta: string }> = [
  { valor: 'DISPONIBLE', etiqueta: 'Disponible' },
  { valor: 'SEPARADA', etiqueta: 'Separada' },
  { valor: 'VENDIDA', etiqueta: 'Vendida' },
];

interface Ajuste {
  unidad: CatalogUnit;
  nuevo: bigint;
}

/**
 * change-70 · CA-3: consulta del catálogo para todo el que tenga `catalogo.ver` (el ADMIN_INMOBILIARIA
 * incluido). CA-4 (HU-A06): «Ajustar precio» solo con `catalogo.editar` (`acceso.editar`).
 */
export function CatalogUnits({ acceso }: { acceso: AccesoAdmin }): React.JSX.Element {
  const { data: proyectos, isLoading: cargandoProyectos, isError: errorProyectos, refetch: recargarProyectos } =
    useCatalogProjects();
  const lista = React.useMemo(() => proyectos?.data ?? [], [proyectos]);

  const [proyectoId, setProyectoId] = React.useState('');
  const [torre, setTorre] = React.useState('');
  const [tipo, setTipo] = React.useState<UnitType | ''>('');
  const [estado, setEstado] = React.useState<UnitStatus | ''>('');
  const [busqueda, setBusqueda] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [editando, setEditando] = React.useState<CatalogUnit | null>(null);
  const [ajuste, setAjuste] = React.useState<Ajuste | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (lista.length === 0) return;
    if (!lista.some((p) => p.id === proyectoId)) setProyectoId(lista[0].id);
  }, [lista, proyectoId]);

  const consulta = proyectoId
    ? {
        proyectoId,
        torre: torre.trim() || undefined,
        tipo: tipo || undefined,
        estado: estado || undefined,
        q: busqueda.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      }
    : null;
  const { data, isLoading, isError, refetch } = useCatalogUnits(consulta);
  const ajustar = useAdjustPrice();

  /** Cualquier cambio de filtro vuelve a la primera página. */
  const filtrar = <T,>(set: React.Dispatch<React.SetStateAction<T>>) => (valor: T) => {
    set(valor);
    setPage(1);
  };

  const columns = React.useMemo<Array<ColumnDef<CatalogUnit, unknown>>>(
    () => [
      // Tablet vertical (AGENTS §28): cuatro columnas; tipo y matrícula van bajo la unidad y las dos
      // áreas comparten columna, para que precio y estado se lean sin desplazar la tabla.
      {
        id: 'unidad',
        header: 'Unidad',
        cell: ({ row }) => (
          <div className="space-y-0.5">
            <span className="block font-medium text-foreground">{etiquetaUnidad(row.original)}</span>
            <span className="block text-xs text-muted-foreground">
              {ETIQUETA_TIPO[row.original.tipo] ?? row.original.tipo} · {row.original.matricula}
            </span>
          </div>
        ),
      },
      {
        id: 'areas',
        header: 'Áreas',
        cell: ({ row }) => (
          <dl className="grid grid-cols-[auto_auto] gap-x-2 whitespace-nowrap text-xs tabular-nums">
            <dt className="text-muted-foreground">Privada</dt>
            <dd className="text-foreground">{formatArea(row.original.areaPrivada)}</dd>
            <dt className="text-muted-foreground">Construida</dt>
            <dd className="text-foreground">{formatArea(row.original.areaConstruida)}</dd>
          </dl>
        ),
      },
      {
        id: 'precioLista',
        header: 'Precio de lista',
        cell: ({ row }) => <span className="whitespace-nowrap font-medium tabular-nums">{formatCOP(row.original.precioLista)}</span>,
      },
      { id: 'estado', header: 'Estado', cell: ({ row }) => <StatusBadge status={row.original.estado} /> },
    ],
    [],
  );

  if (cargandoProyectos) return <LoadingState message="Cargando proyectos" />;
  if (errorProyectos) {
    return <ErrorState message="No fue posible cargar los proyectos" onRetry={() => void recargarProyectos()} />;
  }
  if (lista.length === 0) {
    return <EmptyState title="Aún no hay proyectos" description="Cuando exista un proyecto, aquí verás su catálogo de unidades." />;
  }

  const avisoEstado = (unidad: CatalogUnit): string =>
    unidad.estado === 'DISPONIBLE'
      ? ''
      : ` La unidad está ${unidad.estado === 'SEPARADA' ? 'separada' : 'vendida'}: su separación conserva el precio con el que se emitió.`;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        <div className="grid gap-3 rounded-xl border border-border/70 bg-card p-4 shadow-elegant sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <label htmlFor="catalogo-proyecto" className="block text-sm font-medium text-foreground">
              Proyecto
            </label>
            <select
              id="catalogo-proyecto"
              aria-label="Proyecto"
              className={SELECT_CLASS}
              value={proyectoId}
              onChange={(e) => filtrar(setProyectoId)(String(e.target.value))}
            >
              {lista.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="catalogo-buscar" className="block text-sm font-medium text-foreground">
              Buscar
            </label>
            <Input
              id="catalogo-buscar"
              aria-label="Buscar unidad"
              placeholder="Número o matrícula"
              className="h-11"
              value={busqueda}
              onChange={(e) => filtrar(setBusqueda)(String(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="catalogo-torre" className="block text-sm font-medium text-foreground">
              Torre
            </label>
            <Input
              id="catalogo-torre"
              aria-label="Torre"
              placeholder="Todas"
              className="h-11"
              value={torre}
              onChange={(e) => filtrar(setTorre)(String(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="catalogo-tipo" className="block text-sm font-medium text-foreground">
              Tipo
            </label>
            <select
              id="catalogo-tipo"
              aria-label="Tipo"
              className={SELECT_CLASS}
              value={tipo}
              onChange={(e) => filtrar(setTipo)(e.target.value as UnitType | '')}
            >
              <option value="">Todos</option>
              {TIPOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="catalogo-estado" className="block text-sm font-medium text-foreground">
              Estado
            </label>
            <select
              id="catalogo-estado"
              aria-label="Estado"
              className={SELECT_CLASS}
              value={estado}
              onChange={(e) => filtrar(setEstado)(e.target.value as UnitStatus | '')}
            >
              <option value="">Todos</option>
              {ESTADOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </option>
              ))}
            </select>
          </div>
        </div>

        {feedback && (
          <p role="status" className="rounded-lg border border-border/70 bg-surface-fade px-4 py-3 text-sm text-foreground">
            {feedback}
          </p>
        )}

        {isLoading ? (
          <LoadingState message="Cargando unidades" />
        ) : isError ? (
          <ErrorState message="No fue posible cargar las unidades" onRetry={() => void refetch()} />
        ) : (
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            emptyMessage="No hay unidades que coincidan con la búsqueda"
            manualPagination
            rowCount={data?.meta.total ?? 0}
            pagination={{ pageIndex: page - 1, pageSize: PAGE_SIZE }}
            onPaginationChange={(state) => setPage(state.pageIndex + 1)}
            rowActions={
              acceso.editar
                ? (unidad) => (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Ajustar precio de ${etiquetaUnidad(unidad)}`}
                          onClick={() => {
                            setFeedback(null);
                            setEditando(unidad);
                          }}
                        >
                          <BadgeDollarSign className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ajustar precio</TooltipContent>
                    </Tooltip>
                  )
                : undefined
            }
          />
        )}

        {acceso.editar && (
          <>
            <PriceAdjustDialog
              unidad={editando}
              onOpenChange={(open) => {
                if (!open) setEditando(null);
              }}
              onReview={(unidad, nuevo) => {
                setEditando(null);
                setAjuste({ unidad, nuevo });
              }}
            />
            <ConfirmDialog
              open={Boolean(ajuste)}
              onOpenChange={(open) => {
                if (!open) setAjuste(null);
              }}
              title="Confirmar ajuste de precio"
              description={
                ajuste
                  ? `${etiquetaUnidad(ajuste.unidad)}: ${formatCOP(ajuste.unidad.precioLista)} → ${formatCOP(ajuste.nuevo)}. ` +
                    'El nuevo precio aplica solo a separaciones futuras; las separaciones ya generadas conservan el precio con el que se emitieron.' +
                    avisoEstado(ajuste.unidad)
                  : undefined
              }
              confirmText="Ajustar precio"
              loading={ajustar.isPending}
              onConfirm={() => {
                if (!ajuste) return;
                const { unidad, nuevo } = ajuste;
                ajustar.mutate(
                  { id: unidad.id, precioLista: Number(nuevo) },
                  {
                    onSuccess: () => {
                      setFeedback(`Precio de ${etiquetaUnidad(unidad)} actualizado a ${formatCOP(nuevo)}.`);
                      setAjuste(null);
                    },
                    onError: (error) => {
                      setFeedback(error.message);
                      setAjuste(null);
                    },
                  },
                );
              }}
            />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
